/**
 * Admin Design System
 * ------------------------------------------------------------
 * Single source of truth for the admin panel UI tokens, status
 * palette, tab grouping, and reusable shell primitives. Keeps
 * map pin colors, filter chips, status badges, and count badges
 * in sync across every section.
 */
import React from 'react';
import {
  BarChart3,
  Users,
  UserCircle2,
  MapPin,
  FileText,
  BellRing,
  LifeBuoy,
  Flag,
  ShieldAlert,
  Send,
  Megaphone,
  Sparkles,
  TrendingUp,
  CreditCard,
  X,
  ShieldCheck,
  Crown,
  Scale,
  Bell,
  Navigation,
  Compass,
} from 'lucide-react';

// ---------------------------------------------------------------
// Color palette
// ---------------------------------------------------------------

export const ADMIN_PALETTE = {
  // Brand
  brandRed: '#DD0608',
  ink: '#0F1115',
  inkSoft: '#3B404B',
  inkMuted: '#6B7280',
  // Surfaces
  surface: '#FFFFFF',
  surfaceMuted: '#F6F7F9',
  surfaceTinted: '#F1F2F5',
  border: '#E5E7EB',
  borderStrong: '#D1D5DB',
};

/**
 * Canonical status palette. Every chip, badge, pin, button accent
 * for a given semantic state must use the SAME entry.
 */
export const STATUS_PALETTE = {
  active:   { solid: '#16A34A', soft: '#DCFCE7', text: '#166534', label: 'Active' },
  early:    { solid: '#F59E0B', soft: '#FEF3C7', text: '#92400E', label: 'Early' },
  venue:    { solid: '#4F46E5', soft: '#E0E7FF', text: '#3730A3', label: 'Venue' },
  // Pending used to share a near-identical indigo with `venue` so the
  // two were indistinguishable on the map. Coral red keeps it visually
  // separate from active-venue indigo without crashing into `urgent`
  // (which is a deeper blood-red used for disputes / open reports).
  pending:  { solid: '#FB7185', soft: '#FFE4E6', text: '#9F1239', label: 'Pending' },
  hold:     { solid: '#EAB308', soft: '#FEF9C3', text: '#854D0E', label: 'On hold' },
  urgent:   { solid: '#DD0608', soft: '#FEE2E2', text: '#991B1B', label: 'Urgent' },
  resolved: { solid: '#64748B', soft: '#E2E8F0', text: '#334155', label: 'Resolved' },
  info:     { solid: '#0EA5E9', soft: '#E0F2FE', text: '#075985', label: 'Info' },
  neutral:  { solid: '#6B7280', soft: '#F3F4F6', text: '#374151', label: 'Neutral' },
};

// Map filter id → status palette key. Used by the map explorer.
export const FILTER_TO_STATUS = {
  'active-listings': 'active',
  'early-listings':  'early',
  'active-venues':   'venue',
  'pending-venues':  'pending',
  all:               'neutral',
};

// ---------------------------------------------------------------
// Tab catalogue, grouped by operator intent
// ---------------------------------------------------------------

export const ADMIN_TAB_GROUPS = [
  {
    id: 'operations',
    label: 'Operations',
    tabs: [
      { id: 'dashboard',     label: 'Overview',      icon: BarChart3 },
      { id: 'users',         label: 'Accounts',      icon: Users },
      { id: 'admins',        label: 'Admins',        icon: ShieldCheck },
      { id: 'guests',        label: 'Guests',        icon: UserCircle2 },
      { id: 'notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    tabs: [
      { id: 'venue-apps',    label: 'Venues',      icon: MapPin },
      { id: 'applications',  label: 'Listings',    icon: FileText },
      { id: 'approving',     label: 'Approving',   icon: BellRing },
      { id: 'experiences',   label: 'Experiences', icon: Compass },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    tabs: [
      { id: 'support',       label: 'Support',       icon: LifeBuoy },
      { id: 'reports',       label: 'Reports',       icon: Flag },
      { id: 'disputes',      label: 'Disputes',      icon: ShieldAlert },
      { id: 'broadcasts',    label: 'Broadcasts',    icon: Send },
      { id: 'announcements', label: 'Announce',      icon: Megaphone },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    tabs: [
      { id: 'feedback',  label: 'Feedback', icon: Sparkles },
      { id: 'faq-stats', label: 'FAQ',      icon: TrendingUp },
      { id: 'en-route',  label: 'Directions', icon: Navigation },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    tabs: [
      { id: 'transactions', label: 'Finance', icon: CreditCard },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    tabs: [
      { id: 'agreements',     label: 'Agreements',  icon: FileText },
      { id: 'compliance',     label: 'Compliance',  icon: Scale },
      { id: 'legal-settings', label: 'Legal entity', icon: Scale },
    ],
  },
  // Main-admin-only group. AdminPanel filters this out when the
  // current operator is not a main admin.
  {
    id: 'governance',
    label: 'Governance',
    mainAdminOnly: true,
    tabs: [
      { id: 'main-admins', label: 'Main Admins', icon: Crown },
    ],
  },
];

// Tab id → status palette key used to colour the count badge.
export const TAB_COUNT_TONE = {
  approving:     'pending',   // venue applications awaiting review
  applications:  'early',     // listings needing manual location review
  'venue-apps':  'hold',      // venues parked on admin hold
  experiences:   'pending',   // host-submitted map pins awaiting review
  support:       'urgent',    // open support threads
  reports:       'urgent',    // open venue reports
  disputes:      'urgent',    // open transaction disputes
  feedback:      'info',
};

// ---------------------------------------------------------------
// Reusable shell primitives
// ---------------------------------------------------------------

export const CountBadge = ({ value, tone = 'urgent', active = false, dataTestId }) => {
  if (!value || value <= 0) return null;
  const palette = STATUS_PALETTE[tone] || STATUS_PALETTE.urgent;
  return (
    <span
      data-testid={dataTestId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        borderRadius: 9999,
        background: active ? '#FFFFFF' : palette.solid,
        color: active ? palette.solid : '#FFFFFF',
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: 0.2,
      }}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
};

export const TabPill = ({ tab, active, count, countTone, onClick }) => {
  const Icon = tab.icon;
  return (
    <button
      key={tab.id}
      type="button"
      onClick={onClick}
      data-testid={`admin-tab-${tab.id}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '9px 13px',
        borderRadius: 12,
        border: `1px solid ${active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.border}`,
        background: active ? ADMIN_PALETTE.ink : ADMIN_PALETTE.surface,
        color: active ? '#FFFFFF' : ADMIN_PALETTE.inkSoft,
        fontFamily: "'Manrope', sans-serif",
        fontSize: 13,
        fontWeight: active ? 700 : 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
      }}
    >
      <Icon size={14} strokeWidth={2.1} />
      {tab.label}
      <CountBadge
        value={count}
        tone={countTone || 'urgent'}
        active={active}
        dataTestId={`admin-tab-badge-${tab.id}`}
      />
    </button>
  );
};

export const TabGroupDivider = () => (
  <span
    aria-hidden="true"
    style={{
      width: 1,
      height: 20,
      alignSelf: 'center',
      background: ADMIN_PALETTE.border,
      margin: '0 4px',
      flexShrink: 0,
    }}
  />
);

export const AttentionBanner = ({ items = [], onDismiss }) => {
  // Hide zero-count items + sort by urgency so admins fix red things
  // first. Tone-priority: urgent → hold → pending → early → neutral.
  const TONE_PRIORITY = { urgent: 0, hold: 1, pending: 2, early: 3, venue: 4, active: 5, neutral: 6, info: 7 };
  const visible = items
    .filter((entry) => entry && entry.count > 0)
    .slice()
    .sort((a, b) => {
      const pa = TONE_PRIORITY[a.tone] ?? 99;
      const pb = TONE_PRIORITY[b.tone] ?? 99;
      if (pa !== pb) return pa - pb;
      return (b.count || 0) - (a.count || 0);
    });
  if (!visible.length) return null;
  return (
    <div
      data-testid="admin-attention-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 18px',
        borderRadius: 16,
        background: '#FFFBEB',
        border: '1px solid #FCD34D',
        marginBottom: 14,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700, color: '#92400E', letterSpacing: 0.3, textTransform: 'uppercase' }}>
        Needs attention
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
        {visible.map((entry) => {
          const palette = STATUS_PALETTE[entry.tone || 'urgent'] || STATUS_PALETTE.urgent;
          return (
            <button
              key={entry.id}
              type="button"
              onClick={entry.onClick}
              data-testid={`admin-attention-${entry.id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                borderRadius: 9999,
                border: `1px solid ${palette.solid}33`,
                background: palette.soft,
                color: palette.text,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <CountBadge value={entry.count} tone={entry.tone || 'urgent'} />
              <span>{entry.label}</span>
            </button>
          );
        })}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          data-testid="admin-attention-banner-dismiss"
          aria-label="Dismiss attention banner"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#92400E', padding: 4 }}
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  );
};

/**
 * Reusable EmptyState block — used by Support / Disputes / Reports /
 * Broadcasts / Announcements when their lists are empty. Shows a tinted
 * icon circle, headline, subhead, and an optional CTA. Far more
 * informative than the previous "No X yet." plain text.
 */
export const EmptyState = ({
  icon: Icon,
  tone = 'neutral',
  title,
  body,
  actionLabel,
  onAction,
  dataTestId,
}) => {
  const palette = STATUS_PALETTE[tone] || STATUS_PALETTE.neutral;
  return (
    <div
      data-testid={dataTestId || 'admin-empty-state'}
      style={{
        background: '#FFFFFF',
        border: `1px dashed ${palette.solid}44`,
        borderRadius: 18,
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 10,
      }}
    >
      {Icon ? (
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: palette.soft,
            color: palette.text,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon size={20} strokeWidth={2.2} />
        </span>
      ) : null}
      <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: ADMIN_PALETTE.ink }}>
        {title}
      </p>
      {body ? (
        <p style={{ margin: 0, fontSize: 13, color: ADMIN_PALETTE.inkSoft, maxWidth: 460, lineHeight: 1.45 }}>
          {body}
        </p>
      ) : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          data-testid={`${dataTestId || 'admin-empty-state'}-action`}
          style={{
            marginTop: 4,
            padding: '8px 14px',
            borderRadius: 9999,
            border: 'none',
            background: ADMIN_PALETTE.ink,
            color: '#FFFFFF',
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 700,
          }}
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};

export const StatusChip = ({ tone = 'neutral', label, count, active = false, onClick, dataTestId }) => {
  const palette = STATUS_PALETTE[tone] || STATUS_PALETTE.neutral;
  const interactive = typeof onClick === 'function';
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 12px',
    borderRadius: 9999,
    border: `1px solid ${active ? palette.solid : palette.solid + '40'}`,
    background: active ? palette.solid : palette.soft,
    color: active ? '#FFFFFF' : palette.text,
    fontSize: 12.5,
    fontWeight: 700,
    cursor: interactive ? 'pointer' : 'default',
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      data-testid={dataTestId}
      style={baseStyle}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 9999,
          background: active ? '#FFFFFF' : palette.solid,
          display: 'inline-block',
        }}
      />
      {label}
      {typeof count === 'number' ? (
        <span
          style={{
            background: active ? 'rgba(255,255,255,0.22)' : '#FFFFFF',
            color: active ? '#FFFFFF' : palette.text,
            borderRadius: 9999,
            padding: '1px 8px',
            fontSize: 11,
            fontWeight: 800,
            lineHeight: 1.4,
          }}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
};

export const MapLegend = ({ entries = [] }) => (
  <div
    data-testid="admin-map-legend"
    style={{
      display: 'inline-flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 14,
      padding: '8px 12px',
      borderRadius: 12,
      background: ADMIN_PALETTE.surfaceMuted,
      border: `1px solid ${ADMIN_PALETTE.border}`,
    }}
  >
    <span style={{ fontSize: 11, fontWeight: 800, color: ADMIN_PALETTE.inkMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>
      Legend
    </span>
    {entries.map((entry) => {
      const palette = STATUS_PALETTE[entry.tone] || STATUS_PALETTE.neutral;
      return (
        <span key={entry.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 9999,
              background: palette.solid,
              boxShadow: `0 0 0 2px ${palette.solid}22`,
            }}
          />
          <span style={{ fontSize: 12, color: ADMIN_PALETTE.inkSoft, fontWeight: 600 }}>{entry.label}</span>
        </span>
      );
    })}
  </div>
);
